// src/features/streams/components/StreamSimulcastTab.tsx

import { useCallback, useMemo, useState } from 'react';
import { Stack, Group, Text, Button } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import {
  SimulcastType,
  type ISimulcastAllData,
} from '@/lib/types/Simulcast';
import type { StreamViewData } from '@/lib/types/Stream';
import { useExtPlatAuths, buildAuthMap } from '../useExtPlatAuth';
import {
  useStartSimulcast,
  useStopSimulcast,
  useDeleteSimulcast,
} from '../useSimulcastMutations';
import { SimulcastTransforms } from '../simulcast/simulcastTransforms';
import { DestinationCard, type PendingAction } from './simulcast/DestinationCard';
import { DestinationEmptyState } from './simulcast/DestinationEmptyState';
import { DestinationEditorModal, type ModalState } from './simulcast/DestinationEditorModal';

interface StreamSimulcastTabProps {
  stream: StreamViewData;
}

/** Resolve the correct edit mode for a simulcast's type. */
function editModeForType(simulcast: ISimulcastAllData): ModalState {
  switch (simulcast.core.simulcastType) {
    case SimulcastType.Facebook:
      return { mode: 'edit-facebook', simulcast };
    case SimulcastType.Youtube:
      return { mode: 'edit-youtube', simulcast };
    default:
      return { mode: 'edit-rtmp', simulcast };
  }
}

export function StreamSimulcastTab({ stream }: StreamSimulcastTabProps) {
  const rawSimulcasts = stream.simulcasts ?? [];
  const mediaId = stream.media.mediaId;

  // Sort by simulcastId ascending for stable rendering order across refetches
  const simulcasts = useMemo(
    () => [...rawSimulcasts].sort((a, b) => a.core.simulcastId - b.core.simulcastId),
    [rawSimulcasts],
  );

  // Modal state
  const [modalState, setModalState] = useState<ModalState>({ mode: 'closed' });

  // Per-card pending state: tracks which simulcast has an action in flight
  const [pendingMap, setPendingMap] = useState<Map<number, PendingAction>>(new Map());

  const setPending = useCallback((simulcastId: number, action: PendingAction) => {
    setPendingMap(prev => {
      const next = new Map(prev);
      if (action === null) {
        next.delete(simulcastId);
      } else {
        next.set(simulcastId, action);
      }
      return next;
    });
  }, []);

  // Fetch extPlatAuths only when FB/YT destinations exist (for error resolution)
  const hasFBorYT = simulcasts.some(
    s =>
      s.core.simulcastType === SimulcastType.Facebook ||
      s.core.simulcastType === SimulcastType.Youtube,
  );
  const { data: extPlatAuths, isFetched: authsFetched } = useExtPlatAuths({ enabled: hasFBorYT });
  // When FB/YT destinations don't exist, we never fetch — treat as loaded
  const authsLoaded = !hasFBorYT || authsFetched;

  // Build lookup map for error resolution
  const extPlatAuthMap = useMemo(() => buildAuthMap(extPlatAuths), [extPlatAuths]);

  // Mutation hooks
  const startMutation = useStartSimulcast(mediaId);
  const stopMutation = useStopSimulcast(mediaId);
  const deleteMutation = useDeleteSimulcast(mediaId);

  // --- Handlers ---

  const openCreateModal = useCallback(() => {
    setModalState({ mode: 'create-select-type' });
  }, []);

  const handleStart = useCallback(
    (simulcast: ISimulcastAllData) => {
      const simulcastId = simulcast.core.simulcastId;
      const name = SimulcastTransforms.getDestinationLabel(simulcast);
      setPending(simulcastId, 'start');
      startMutation.mutate(simulcastId, {
        onSuccess: () => {
          notifications.show({
            title: 'Started',
            message: `"${name}" is now active.`,
            color: 'green',
          });
        },
        onError: (err) => {
          notifications.show({
            title: 'Error',
            message: err.message,
            color: 'red',
          });
        },
        onSettled: () => setPending(simulcastId, null),
      });
    },
    [startMutation, setPending],
  );

  const handleStop = useCallback(
    (simulcast: ISimulcastAllData) => {
      const simulcastId = simulcast.core.simulcastId;
      const name = SimulcastTransforms.getDestinationLabel(simulcast);
      setPending(simulcastId, 'stop');
      stopMutation.mutate(simulcastId, {
        onSuccess: () => {
          notifications.show({
            title: 'Stopped',
            message: `"${name}" is now inactive.`,
            color: 'green',
          });
        },
        onError: (err) => {
          notifications.show({
            title: 'Error',
            message: err.message,
            color: 'red',
          });
        },
        onSettled: () => setPending(simulcastId, null),
      });
    },
    [stopMutation, setPending],
  );

  const handleDelete = useCallback(
    (simulcast: ISimulcastAllData) => {
      const name = SimulcastTransforms.getDestinationLabel(simulcast);
      modals.openConfirmModal({
        title: 'Delete Destination',
        children: (
          <Text size="sm">
            Are you sure you want to delete &ldquo;{name}&rdquo;? This action cannot be
            undone.
          </Text>
        ),
        labels: { confirm: 'Delete', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          const simulcastId = simulcast.core.simulcastId;
          setPending(simulcastId, 'delete');
          deleteMutation.mutate(simulcastId, {
            onSuccess: () => {
              notifications.show({
                title: 'Deleted',
                message: `"${name}" has been deleted.`,
                color: 'green',
              });
            },
            onError: (err) => {
              notifications.show({
                title: 'Error',
                message: err.message,
                color: 'red',
              });
            },
            onSettled: () => setPending(simulcastId, null),
          });
        },
      });
    },
    [deleteMutation, setPending],
  );

  const handleEdit = useCallback((simulcast: ISimulcastAllData) => {
    setModalState(editModeForType(simulcast));
  }, []);

  const handleReconnect = useCallback((simulcast: ISimulcastAllData) => {
    const type = simulcast.core.simulcastType;
    if (type === SimulcastType.Facebook) {
      setModalState({ mode: 'reconnect-facebook', simulcast });
    } else if (type === SimulcastType.Youtube) {
      setModalState({ mode: 'reconnect-youtube', simulcast });
    }
  }, []);

  if (simulcasts.length === 0) {
    return (
      <>
        <DestinationEmptyState onAdd={openCreateModal} />
        <DestinationEditorModal
          state={modalState}
          onClose={() => setModalState({ mode: 'closed' })}
          onChangeMode={setModalState}
          stream={stream}
        />
      </>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text fw={600} size="lg">
          Destinations
        </Text>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={openCreateModal}
        >
          Add Destination
        </Button>
      </Group>

      {simulcasts.map(sim => (
        <DestinationCard
          key={sim.core.simulcastId}
          simulcast={sim}
          extPlatAuth={extPlatAuthMap.get(sim.core.extPlatAuthId ?? -1)}
          authsLoaded={authsLoaded}
          pendingAction={pendingMap.get(sim.core.simulcastId) ?? null}
          onStart={handleStart}
          onStop={handleStop}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onReconnect={handleReconnect}
        />
      ))}

      <DestinationEditorModal
        state={modalState}
        onClose={() => setModalState({ mode: 'closed' })}
        onChangeMode={setModalState}
        stream={stream}
      />
    </Stack>
  );
}
